import { SEASON_AGNOSTIC } from '../../constants/seasonIds';
import {
  FHA_KEY,
  CROP_WATER_KEY,
  SEASON_AGNOSTIC_IDS,
 WEATHER } from '../../constants/mapLayerIds';
import { extractFHALayers } from './mosaic/fha';
import { extractCropWaterLayers } from './mosaic/cropWaterUse';
import { extractPlantingLayers } from './planting';
import { extractHarvestLayers } from './harvest';
import { extractApplicationLayers } from './applications';
import { getSoilsSurgolayersValues } from './mosaic/soilSsurgo';

/**
 * Determine if the layer is season-agnostic
 * @param {String} layer - Layer ID
 * @return {Object}
 */
export function isLayerSeasonAgnostic(layer) {
  return SEASON_AGNOSTIC_IDS.includes(layer);
}

/**
 * Return the specified layer object held within state.
 * The layer can be season-specific or season-agnostic.
 * @param {Object} list - Layers list
 * @param {String} layer - Layer ID
 * @param {String} [season] - Season tag
 * @return {Object}
 */
export function getCurrLayer(list, layer, season) {
  const seasonId = isLayerSeasonAgnostic(layer) ? SEASON_AGNOSTIC : season;
  return list[seasonId][layer];
}

/**
 * Extract the available map layers from the season data, mosaic events, and field permission
 * IMPORTANT! Any layers that have sub-layers should have default activeSubLayerIds values.
 * For example, see the extractApplicationLayers function below.
 * @param {Object} seasons
 * @param {Array} sortedSeasonKeys
 * @param {String} fieldPermission
 * @param {Object} mosaicLayers
 * @param {Object} mosaicLayers.seasonAgnostic
 * @param {Object} mosaicLayers.seasonSpecific
 * @param {Object} crops
 * @param {Array} productAssets
 * @param {Array} scoutingActivities
 * @return {Object}
 */
export function extractMapLayers(
  seasons,
  sortedSeasonKeys,
  fieldPermission,
  mosaicLayers,
  crops,
  productAssets = []
) {
  // Season-Agnostic Layers
  const seasonAgnosticLayers = {
    ...getSoilsSurgolayersValues,
    ...mosaicLayers.seasonAgnostic,
  };

  // Season-Specific Layers
  const fhaData = mosaicLayers.seasonSpecific[FHA_KEY] || {};
  const cropWaterData = mosaicLayers.seasonSpecific[CROP_WATER_KEY] || {};
  const seasonSpecificLayers = sortedSeasonKeys.reduce(
    (result, season) => ({
      ...result,
      [season]: {
        [WEATHER]: {},
        ...extractFHALayers(seasons[season], fhaData, productAssets),
        ...extractCropWaterLayers(
          seasons[season],
          cropWaterData,
          productAssets
        ),
        ...extractPlantingLayers(seasons[season], season, crops),
        ...extractHarvestLayers(seasons[season], fieldPermission),
        ...extractApplicationLayers(seasons[season]),
      },
    }),
    {}
  );

  return {
    [SEASON_AGNOSTIC]: seasonAgnosticLayers,
    ...seasonSpecificLayers,
  };
}
